/**
 * @fileoverview Market page — rating band & value badge bucketing
 * @module utils/marketBuckets
 *
 * Pure mapping helpers. Rating bands are a NEW player-facing grouping
 * (numeric threshold buckets) requested specifically for the Market page —
 * they are independent of the existing internal `ratingBand` categorical
 * field (DOMINANT/ELITE_CHANCE/etc) janus_rating_engine_v4 writes onto
 * runners, which is tuned for in-race context rather than cross-race
 * market analytics.
 *
 * Value buckets DO map onto the existing `valueBadge` convention
 * (UNKNOWN|BARGAIN|VALUE|FAIR|PRICEY|RISK) — see showdown-functions/
 * value_signal.js — just relabelled for player-friendly display
 * (PRICEY -> Premium) rather than inventing a new taxonomy.
 */

export const RATING_BANDS = ['90+', '80-89', '70-79', '60-69', 'Below 60', 'Unknown'];

/**
 * @param {number|null|undefined} displayRating - 1-99 public rating
 * @returns {string} one of RATING_BANDS
 */
export function getRatingBand(displayRating) {
  const r = Number(displayRating);
  if (displayRating == null || !Number.isFinite(r)) return 'Unknown';
  if (r >= 90) return '90+';
  if (r >= 80) return '80-89';
  if (r >= 70) return '70-79';
  if (r >= 60) return '60-69';
  return 'Below 60';
}

export const VALUE_BUCKETS = ['Bargain', 'Value', 'Fair', 'Premium', 'Risk', 'Unknown'];

const VALUE_BADGE_TO_BUCKET = {
  BARGAIN: 'Bargain',
  VALUE:   'Value',
  FAIR:    'Fair',
  PRICEY:  'Premium',
  RISK:    'Risk',
  UNKNOWN: 'Unknown',
};

/**
 * @param {string|null|undefined} valueBadge - raw runner.valueBadge field
 * @returns {string} one of VALUE_BUCKETS
 */
export function getValueBucket(valueBadge) {
  if (!valueBadge) return 'Unknown';
  const key = String(valueBadge).trim().toUpperCase();
  return VALUE_BADGE_TO_BUCKET[key] || 'Unknown';
}

/** Sorts group rows (rating bands or value buckets) into the canonical order above. */
export function sortByBucketOrder(rows, order) {
  return [...(rows || [])].sort((a, b) => {
    const ia = order.indexOf(a.groupKey);
    const ib = order.indexOf(b.groupKey);
    return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib);
  });
}
